/** Web Speech API wrapper. Supports the standard SpeechRecognition and
 *  the older webkitSpeechRecognition. iOS Safari has known reliability
 *  issues - callers degrade with a notice when isSpeechSupported() is
 *  false instead of breaking. */

// Web Speech API types are not in the default DOM lib for all targets;
// reach into window.* defensively at runtime.
type SpeechCtor = new () => SpeechRecognitionLike;
type AnyWindow = typeof window & {
  SpeechRecognition?: SpeechCtor;
  webkitSpeechRecognition?: SpeechCtor;
};

export type SpeechRecognitionLike = {
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((event: any) => void) | null;
  onend: (() => void) | null;
  onerror: ((event: any) => void) | null;
  continuous: boolean;
  interimResults: boolean;
  lang: string;
};

export function isSpeechSupported(): boolean {
  if (typeof window === "undefined") return false;
  const w = window as AnyWindow;
  return !!(w.SpeechRecognition || w.webkitSpeechRecognition);
}

export function createRecognition(): SpeechRecognitionLike | null {
  if (typeof window === "undefined") return null;
  const w = window as AnyWindow;
  const Ctor = w.SpeechRecognition || w.webkitSpeechRecognition;
  if (!Ctor) return null;
  const r = new Ctor();
  r.continuous = true;
  r.interimResults = true;
  r.lang = navigator.language || "en-US";
  return r;
}

/** Convenience: start recognition + return a stop() function and a
 *  promise that resolves with the final transcript when stop() fires
 *  or the engine ends naturally. Streaming intermediate transcripts
 *  flow through the onPartial callback so the UI can show what's
 *  being heard live. */
export function startListening(opts: {
  onPartial?: (text: string) => void;
  onError?: (msg: string) => void;
}): { stop: () => void; done: Promise<string> } | null {
  const recognition = createRecognition();
  if (!recognition) return null;

  let finalText = "";
  let interim = "";
  let resolved = false;
  let resolveDone!: (text: string) => void;
  const done = new Promise<string>((res) => {
    resolveDone = res;
  });

  recognition.onresult = (event: any) => {
    interim = "";
    for (let i = event.resultIndex; i < event.results.length; i++) {
      const result = event.results[i];
      const transcript = String(result[0]?.transcript || "");
      if (result.isFinal) {
        finalText = (finalText + " " + transcript).trim();
      } else {
        interim += transcript;
      }
    }
    opts.onPartial?.((finalText + " " + interim).trim());
  };
  recognition.onend = () => {
    if (resolved) return;
    resolved = true;
    resolveDone((finalText + " " + interim).trim());
  };
  recognition.onerror = (event: any) => {
    opts.onError?.(String(event?.error || "speech-error"));
    if (resolved) return;
    resolved = true;
    resolveDone((finalText + " " + interim).trim());
  };

  try {
    recognition.start();
  } catch (err) {
    opts.onError?.(String(err));
    return null;
  }

  return {
    stop: () => recognition.stop(),
    done,
  };
}
